import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { notificationAPI, swipeAPI } from "../services/api";
import { useUserProfileModal } from "../context/UserProfileModalContext";

function NotificationDropdown({ isOpen, onClose, onMarkedRead }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { openUserProfile } = useUserProfileModal();

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.getNotifications();
      const items = response.data.data || [];
      setNotifications(items);

      // Mark all as read when dropdown opens
      if (items.length > 0) {
        await notificationAPI.markAllAsRead();
        setNotifications((prev) =>
          prev.map((item) => ({ ...item, isRead: true })),
        );
        if (onMarkedRead) {
          onMarkedRead();
        }
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [onMarkedRead]);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleNotificationClick = async (notification) => {
    if (notification.type === "RIGHT_SWIPE") {
      const userId = notification.fromUserId?.toString?.();
      if (userId) {
        // Close dropdown first
        onClose();

        // Check swipe status before opening modal
        setCheckingStatus(true);
        try {
          const response = await swipeAPI.getSwipeStatus(userId);
          const statusData = response.data.data;

          const canSwipe =
            !statusData?.status ||
            (statusData.status !== "liked" &&
              statusData.status !== "match" &&
              statusData.status !== "passed");

          const handleLike = async () => {
            try {
              const likeResponse = await swipeAPI.like(userId);
              if (likeResponse.data?.success) {
                if (
                  likeResponse.data.data?.isMatch &&
                  likeResponse.data.data?.matchId
                ) {
                  navigate(`/chat/${likeResponse.data.data.matchId}`);
                }
              }
            } catch (error) {
              console.error("Error liking user:", error);
            }
          };

          const handleSkip = async () => {
            try {
              await swipeAPI.skip(userId);
            } catch (error) {
              console.error("Error skipping user:", error);
            }
          };

          openUserProfile({
            userId,
            swipeStatus: statusData,
            onLike: canSwipe ? handleLike : undefined,
            onSkip: canSwipe ? handleSkip : undefined,
            disableActions: false,
            prefetchedProfile: notification.fromUserProfile || null,
          });
        } catch (error) {
          console.error("Error checking swipe status:", error);
        } finally {
          setCheckingStatus(false);
        }

        return;
      }
    }

    if (notification.matchId) {
      // Both match and message notifications should have matchId
      navigate(`/chat/${notification.matchId}`);
      onClose();
    }
  };

  // Like/Skip handlers now live inside handleNotificationClick via
  // the global UserProfileModal context.

  const getNotificationText = (notification) => {
    const userName = notification.fromUserProfile?.name || "Someone";

    if (notification.type === "match") {
      return `You matched with ${userName}!`;
    } else if (notification.type === "message") {
      return `New message from ${userName}`;
    } else if (notification.type === "RIGHT_SWIPE") {
      return "Someone swiped you right ❤️";
    }
    return "New notification";
  };

  const getNotificationIcon = (notification) => {
    if (notification.type === "match") {
      return "💕";
    } else if (notification.type === "message") {
      return "💬";
    } else if (notification.type === "RIGHT_SWIPE") {
      return "❤️";
    }
    return "🔔";
  };

  const formatTime = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return notificationDate.toLocaleDateString();
  };

  return (
    <>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[500px] overflow-hidden z-[100]"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Notifications</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto max-h-[440px]">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="w-12 h-12 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">No new notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.isRead ? "bg-blue-50" : ""
                    } ${checkingStatus ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* User Photo or Icon */}
                      <div className="flex-shrink-0">
                        {notification.fromUserProfile?.photos?.[0] ? (
                          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200">
                            <img
                              src={notification.fromUserProfile.photos[0]}
                              alt={notification.fromUserProfile.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white text-lg">
                            {getNotificationIcon(notification)}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 font-medium">
                          {getNotificationText(notification)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTime(notification.createdAt)}
                        </p>
                      </div>

                      {/* Unread indicator */}
                      {!notification.isRead && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer - View All */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  navigate("/chats");
                  onClose();
                }}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition"
              >
                View All Chats
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default NotificationDropdown;
